import { useState } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const leftCategories = categories.slice(0, 5);
  const middleCategories = categories.slice(5, 10);
  const rightCategories = categories.slice(10);
  const [email, setEmail] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitStatus("submitting");
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      const response = await fetch(
        "https://readdy.ai/api/form/d8pcmjjf86hv9evgu2g0",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: formData.toString(),
        }
      );
      if (response.ok) {
        setSubmitStatus("success");
        setEmail("");
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    }
  };

  return (
    <footer className="bg-secondary-50 border-t border-background-200/70">
      <div className="px-4 md:px-6 lg:px-10 py-14 md:py-16">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          <div className="lg:w-[40%]">
            <Link to="/" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              SugarGoo<br />Spreadsheet
            </Link>
            <p className="mt-5 text-sm text-foreground-600 leading-relaxed max-w-sm">
              Your curated gateway to the best Sugargoo spreadsheet shopping experience. Discover hand-picked product links across every category, updated regularly for quality and accuracy.
            </p>
            <div className="mt-6">
              <p className="text-xs font-medium text-foreground-500 uppercase tracking-wide mb-3">
                Subscribe for updates
              </p>
              <form
                id="footer-subscribe"
                className="flex flex-col sm:flex-row gap-2"
                data-readdy-form="footer-subscribe"
                onSubmit={handleSubmit}
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 bg-transparent border-b border-foreground-300 px-1 py-2 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitStatus === "submitting"}
                  className="whitespace-nowrap px-5 py-2 border border-foreground-300 rounded-full text-sm font-medium text-foreground-700 hover:border-primary-500 hover:text-primary-600 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {submitStatus === "submitting" ? "Sending..." : "Join"}
                </button>
              </form>
              {submitStatus === "success" && (
                <p className="mt-2 text-xs text-accent-700">Thanks for subscribing!</p>
              )}
              {submitStatus === "error" && (
                <p className="mt-2 text-xs text-primary-600">Something went wrong. Please try again.</p>
              )}
            </div>
          </div>

          <div className="lg:w-[60%] grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div>
              <h4 className="text-sm font-semibold text-foreground-950 mb-4">
                Categories
              </h4>
              <ul className="space-y-2.5">
                {leftCategories.map((cat) => (
                  <li key={cat.name}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground-950 mb-4">
                &nbsp;
              </h4>
              <ul className="space-y-2.5">
                {middleCategories.map((cat) => (
                  <li key={cat.name}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground-950 mb-4">
                Quick Links
              </h4>
              <ul className="space-y-2.5">
                {rightCategories.map((cat) => (
                  <li key={cat.name}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
                <li>
                  <Link
                    to="/blog/sugargoo-spreadsheet-guide"
                    className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors"
                  >
                    SEO Blog
                  </Link>
                </li>
                <li>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    Main Store →
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-background-200/60 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} SugarGoo Spreadsheet. All rights reserved.
          </p>
          <p className="text-xs text-foreground-400">
            Educational resource only. Not affiliated with Sugargoo.
          </p>
        </div>
      </div>
    </footer>
  );
}